import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Payment } from '../../../core/types';
import { usePaymentStore } from '../store/paymentStore';
import { AppBar, LoadingWidget, ShimmerAnimation, ZeroState, FadeInAnimation, StatusChip } from '../../../core/components/ui';

// ── PaymentHistoryPage (history.dart equivalent) ─────────────
// export const PaymentHistoryPage: React.FC = () => {
//   const navigate = useNavigate();
//   const location = useLocation();
//   const paymentId =
//     (location.state as any)?.paymentId ||
//     new URLSearchParams(location.search).get('paymentId') ||
//     sessionStorage.getItem('therapp_pending_payment_id');
//   const paymentStore = usePaymentStore();

//   const [status, setStatus] = useState<string>('Checking...');
//   const [loading, setLoading] = useState(true);

//   useEffect(() => {
//     if (paymentId) {
//       checkStatus();
//     } else {
//       setStatus('No payment ID found.');
//       setLoading(false);
//     }
//   }, []);

//   const checkStatus = async () => {
//     setLoading(true);
//     try {
//       const result = await paymentStore.checkPaymentStatus(paymentId);
//       setStatus(result?.status || result?.message || 'Payment completed.');
//     } catch {
//       setStatus('Could not verify payment.');
//     } finally {
//       setLoading(false);
//     }
//   };

//   const isSuccess = status.toLowerCase().includes('success') || status.toLowerCase().includes('completed');

//   return (
//     <div className="page">
//       {loading && <LoadingWidget />}
//       <AppBar title="Payment Status" onBack={() => navigate(-1)} />

//       <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
//         <FadeInAnimation delay={0}>
//           <div style={{ textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16 }}>
//             <div style={{ fontSize: 64 }}>
//               {loading ? '⏳' : isSuccess ? '✅' : '❌'}
//             </div>
//             <h2 style={{ fontSize: 20, fontWeight: 700 }}>
//               {loading ? 'Verifying Payment' : isSuccess ? 'Payment Successful' : 'Payment Pending'}
//             </h2>
//             <p style={{ fontSize: 14, color: 'var(--color-on-surface-variant)', lineHeight: 1.5, maxWidth: 280 }}>
//               {status}
//             </p>
//             <button
//               className="btn-primary"
//               style={{ marginTop: 16, width: 200 }}
//               onClick={() => navigate('/payment-history2')}
//             >
//               Back to Home
//             </button>
//           </div>
//         </FadeInAnimation>
//       </div>
//     </div>
//   );
// };

// ── PaymentHistory2Page (history2.dart equivalent – full list) ─
export const PaymentHistory2Page: React.FC = () => {
  const navigate = useNavigate();
  const paymentStore = usePaymentStore();
  const [payments, setPayments] = useState<Payment[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    const result = await paymentStore.userPayments();
    setPayments(Array.isArray(result) ? result : []); // ← guard here
    setLoading(false);
  };

  const formatDateTime = (dateStr?: string) => {
    if (!dateStr) return '—';
    const date = new Date(dateStr);
    const datePart = date.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
    const timePart = date.toLocaleTimeString(undefined, {
      hour: '2-digit',
      minute: '2-digit',
    });
    return `${datePart} · ${timePart}`;
  };

  return (
    <div className="page">
      <AppBar title="Payment History"
        onBack={() => navigate(-1)}
      />

      {loading ? (
        <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
          {[0, 1, 2, 3].map(i => (
            <div key={i} className="card" style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
              <ShimmerAnimation width={40} height={40} borderRadius="10px" />
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 7 }}>
                <ShimmerAnimation width="55%" height={14} />
                <ShimmerAnimation width="38%" height={11} />
              </div>
              <ShimmerAnimation width={60} height={22} borderRadius="99px" />
            </div>
          ))}
        </div>
      ) : payments.length === 0 ? (
        <ZeroState title="No Payments" message="You haven't made any payments yet." />
      ) : (
        <div style={{ flex: 1, overflowY: 'auto', padding: '16px 16px 0', display: 'flex', flexDirection: 'column', gap: 10 }}>
          {payments.map((p, i) => (
            <FadeInAnimation key={p.id || i} delay={i * 0.06}>
<div
  className="card"
  role="button"
  tabIndex={0}
  onClick={() =>
    navigate('/payment-details', {
      state: { payment: p },
    })
  }
  onKeyDown={(e) => {
    if (e.key === 'Enter') {
      navigate('/payment-details', {
        state: { payment: p },
      });
    }
  }}
  style={{
    display: 'flex',
    alignItems: 'center',
    gap: 14,
    cursor: 'pointer',
  }}
>                <div style={{
                  width: 40, height: 40, borderRadius: 10,
                  background: 'var(--color-primary-container)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                }}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--color-primary)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="1" y="4" width="22" height="16" rx="2"/><line x1="1" y1="10" x2="23" y2="10"/>
                  </svg>
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600, fontSize: 14 }}>
                    {p.currency || 'GHS'} {(p.amount || 0).toLocaleString()}
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--color-on-surface-variant)', marginTop: 2 }}>
                    {formatDateTime(p.createdAt)}
                  </div>
                </div>
                <StatusChip status={p.status || 'PENDING'} />
              </div>
            </FadeInAnimation>
          ))}
          <div style={{ height: 16 }} />
        </div>
      )}
    </div>
  );
};
